import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Heart, Award, Compass, Star, ArrowRight } from "lucide-react";
import { BRAND } from "@/lib/brand";
import useSeo from "@/lib/useSeo";
import LocalTrust from "@/components/LocalTrust";

const values = [
  { icon: ShieldCheck, title: "Honesty Always",    desc: "Transparent matrix pricing — no surprise upcharges, ever." },
  { icon: Heart,       title: "Genuine Care",      desc: "We treat your home like our own. Period." },
  { icon: Award,       title: "Pride in Craft",    desc: "If a job's not perfect, we don't leave until it is." },
  { icon: Compass,     title: "Local Roots",       desc: "Born and built in Evansville. Your neighbors, your team." },
];

export default function AboutPage() {
  useSeo({
    title: "About — Top-Notch Pro Solutions, Evansville's 5-Star Home Care Team",
    description: "Locally owned and family-run, Top-Notch Pro Solutions delivers elite residential cleaning, handyman, and property maintenance services to Evansville, IN and the Tri-State Area within a 20-mile radius.",
  });
  return (
    <div data-testid="about-page">
      <section className="relative bg-[var(--brand-teal)] text-white py-20 md:py-28">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,169,106,0.4), transparent 50%), radial-gradient(circle at 80% 70%, rgba(201,169,106,0.3), transparent 50%)" }} />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8">
          <span className="text-[var(--brand-gold)] section-eyebrow">About Top-Notch Pro Solutions</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>
            A 5-Star Promise, Backed By <br />
            <span className="text-[var(--brand-gold)]">A Family That Cares.</span>
          </h1>
          <p className="mt-6 text-lg text-white/85 max-w-3xl leading-relaxed">
            Top-Notch Pro Solutions was founded on one simple belief: homeowners deserve a property care company that
            actually shows up on time, treats their home with respect, and delivers a perfect result — every single visit.
            We're not a giant franchise. We're your neighbors, and our reputation is everything.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="section-eyebrow">Our Story</span>
            <h2 className="heading-display mt-3 text-3xl md:text-4xl">Built On Punctuality. Powered By Pride.</h2>
            <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
              <p>
                In a service industry crowded with no-shows, hidden fees, and rushed work, we set out to do the opposite.
                Show up on time. Quote it fair. Do it right. Clean up after.
              </p>
              <p>
                Today, we proudly serve homeowners and property managers across Evansville and the surrounding Tri-State —
                from cozy cottages to multi-acre estates — with property maintenance, residential cleaning, and expert handyman care.
                Our instant online booking is available for cleanings within 20 miles of Evansville; for everything else, we'd love to put together a personalized quote.
              </p>
              <p className="font-semibold text-[var(--brand-teal)]">
                We focus on what we do best. That's why we don't offer plumbing or electrical — those deserve licensed specialists.
              </p>
            </div>
            <Link to="/quote" className="btn-primary mt-8" data-testid="about-cta-quote">
              Book Your First Visit <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="aspect-square rounded-3xl overflow-hidden shadow-xl">
              <img src="https://images.unsplash.com/photo-1572120360610-d971b9d7767c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Professional service" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 card-elevated p-5 max-w-[240px] hidden md:block">
              <div className="flex items-center gap-1 text-[var(--brand-gold)] mb-2">
                {["s1", "s2", "s3", "s4", "s5"].map((id) => <Star key={id} className="w-4 h-4 fill-[var(--brand-gold)]" />)}
              </div>
              <p className="text-2xl font-extrabold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>5.0★</p>
              <p className="text-xs text-slate-500">Perfect customer rating</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-[#e6e0d4] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="section-eyebrow">What We Stand For</span>
            <h2 className="heading-display mt-3 text-4xl md:text-5xl">Four Promises. Zero Excuses.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div key={v.title} className="card-elevated p-7" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 rounded-xl bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] flex items-center justify-center mb-5">
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-[var(--brand-teal)] text-lg mb-2" style={{ fontFamily: "Outfit" }}>{v.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LocalTrust variant="about" />

      <section className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
        <h2 className="heading-display text-3xl md:text-4xl">Serving Evansville & Surrounding Areas.</h2>
        <p className="mt-4 text-slate-600">{BRAND.address}</p>
        <p className="mt-1 text-slate-600">Instant online booking for cleanings within 20 miles · {BRAND.phone}</p>
      </section>
    </div>
  );
}
